import { useState } from "react";
import { useExpectedIssues, useLateIssues, useReceiveIssue, useClaimIssue } from "@/api/hooks";
import type { IssueStatus } from "@/api/types";

const STATUS_LABELS: Record<IssueStatus, string> = {
  expected: "Attendu",
  received: "Reçu",
  late: "En retard",
  missing: "Manquant",
  claimed: "Réclamé",
  not_published: "Non publié",
};

const STATUS_COLORS: Record<IssueStatus, string> = {
  expected: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
  received: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  late: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
  missing: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  claimed: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400",
  not_published: "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400",
};

export function SerialControlPage() {
  const [daysAhead, setDaysAhead] = useState(14);
  const [activeTab, setActiveTab] = useState<"expected" | "late">("expected");

  const { data: expectedIssues, isLoading: loadingExpected } = useExpectedIssues(daysAhead);
  const { data: lateIssues, isLoading: loadingLate } = useLateIssues();

  const receiveIssue = useReceiveIssue();
  const claimIssue = useClaimIssue();

  const handleReceive = async (issueId: string) => {
    try {
      await receiveIssue.mutateAsync({ id: issueId });
    } catch (error) {
      console.error("Failed to receive issue:", error);
    }
  };

  const handleClaim = async (issueId: string) => {
    try {
      await claimIssue.mutateAsync(issueId);
    } catch (error) {
      console.error("Failed to claim issue:", error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Bulletinage</h1>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
          <p className="text-sm text-gray-500 dark:text-gray-400">Numéros attendus</p>
          <p className="mt-1 text-2xl font-semibold text-blue-600 dark:text-blue-400">
            {expectedIssues?.count ?? 0}
          </p>
          <p className="mt-1 text-xs text-gray-400">dans les {daysAhead} prochains jours</p>
        </div>
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-900 dark:bg-yellow-900/20">
          <p className="text-sm text-yellow-600 dark:text-yellow-400">Numéros en retard</p>
          <p className="mt-1 text-2xl font-semibold text-yellow-600 dark:text-yellow-400">
            {lateIssues?.count ?? 0}
          </p>
          <p className="mt-1 text-xs text-yellow-500">nécessitent une action</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
          <p className="text-sm text-gray-500 dark:text-gray-400">Horizon de prévision</p>
          <select
            value={daysAhead}
            onChange={(e) => {
              setDaysAhead(parseInt(e.target.value, 10));
            }}
            className="mt-1 rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          >
            <option value={7}>7 jours</option>
            <option value={14}>14 jours</option>
            <option value={30}>30 jours</option>
            <option value={60}>60 jours</option>
          </select>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <div className="flex gap-8">
          <button
            onClick={() => {
              setActiveTab("expected");
            }}
            className={`border-b-2 pb-3 text-sm font-medium transition-colors ${
              activeTab === "expected"
                ? "border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400"
                : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400"
            }`}
          >
            Numéros attendus ({expectedIssues?.count ?? 0})
          </button>
          <button
            onClick={() => {
              setActiveTab("late");
            }}
            className={`border-b-2 pb-3 text-sm font-medium transition-colors ${
              activeTab === "late"
                ? "border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400"
                : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400"
            }`}
          >
            En retard ({lateIssues?.count ?? 0})
          </button>
        </div>
      </div>

      {/* Content */}
      {activeTab === "expected" && (
        <div>
          {loadingExpected ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
            </div>
          ) : expectedIssues && expectedIssues.issues.length > 0 ? (
            <div className="divide-y divide-gray-200 rounded-lg border border-gray-200 bg-white dark:divide-gray-700 dark:border-gray-700 dark:bg-gray-800">
              {expectedIssues.issues.map((issue) => (
                <div key={issue.id} className="flex items-center justify-between p-4">
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-white">
                      {issue.subscriptionTitle}
                    </h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      {issue.displayLabel} • Attendu le{" "}
                      {new Date(issue.expectedDate).toLocaleDateString("fr-FR")}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                      {issue.copiesExpected} exemplaire{issue.copiesExpected > 1 ? "s" : ""} attendu
                      {issue.copiesExpected > 1 ? "s" : ""}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      void handleReceive(issue.id);
                    }}
                    disabled={receiveIssue.isPending}
                    className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:outline-none disabled:opacity-50"
                  >
                    Réceptionner
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-12 text-center">
              <p className="text-gray-500 dark:text-gray-400">
                Aucun numéro attendu dans les {daysAhead} prochains jours
              </p>
            </div>
          )}
        </div>
      )}

      {activeTab === "late" && (
        <div>
          {loadingLate ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
            </div>
          ) : lateIssues && lateIssues.issues.length > 0 ? (
            <div className="divide-y divide-gray-200 rounded-lg border border-gray-200 bg-white dark:divide-gray-700 dark:border-gray-700 dark:bg-gray-800">
              {lateIssues.issues.map((issue) => (
                <div key={issue.id} className="flex items-center justify-between p-4">
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-white">
                      {issue.subscriptionTitle}
                    </h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      {issue.displayLabel} • Attendu le{" "}
                      {new Date(issue.expectedDate).toLocaleDateString("fr-FR")}
                    </p>
                    <span
                      className={`mt-1 inline-block rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[issue.status as IssueStatus]}`}
                    >
                      {STATUS_LABELS[issue.status as IssueStatus]}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        void handleReceive(issue.id);
                      }}
                      disabled={receiveIssue.isPending}
                      className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:outline-none disabled:opacity-50"
                    >
                      Réceptionner
                    </button>
                    <button
                      onClick={() => {
                        void handleClaim(issue.id);
                      }}
                      disabled={claimIssue.isPending}
                      className="rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700 focus:ring-2 focus:ring-purple-500 focus:outline-none disabled:opacity-50"
                    >
                      Réclamer
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-12 text-center">
              <p className="text-gray-500 dark:text-gray-400">Aucun numéro en retard</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default SerialControlPage;
